/**
 * WebRTC mesh session for one call.
 *
 * Signalling travels on the `call:<id>` Phoenix topic; media is peer-to-peer
 * between participants. See protocol/calls-v1.md and
 * protocol/calls-v2-screen-sharing.md.
 *
 * What the first version got wrong, and this one holds to: a candidate that
 * arrives before the answer is kept, not thrown; "connected" is said only
 * when media is known to be flowing, per peer; a peer that fails is retried
 * with an ICE restart rather than left silent; someone who leaves is taken
 * off the screen; and a muted microphone is announced so the others can see
 * it rather than wonder.
 *
 * A shared screen is a second video track on the same connections, never a
 * second connection. Two things follow. The receiver cannot tell which of a
 * peer's streams is the screen by looking at it, so the sharer names its
 * stream id when it claims the surface and the server hands that name to
 * everyone. And a peer that never said it could render one is not sent it at
 * all — an older client keys remote media by user, so a screen would quietly
 * replace the face it was showing.
 */

import { Socket, type Channel } from 'phoenix'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchIceServers, joinCall, leaveCall } from '@/api/calls'
import { api } from '@/api/client'
import type { Call, CallShareSource, UUID } from '@/api/types'
import { PendingCandidates, departedPeerIds, joinedPeerIds } from '@/hooks/callPeers'

interface SocketTokenResponse {
  token: string
}

export interface RemoteTrack {
  userId: UUID
  stream: MediaStream
}

/** What a peer last said about its own microphone and camera. */
export interface RemoteMedia {
  audioMuted: boolean
  videoEnabled: boolean
}

/** Who holds the call's one shared surface, as the server last said. */
export interface ShareState {
  userId: UUID
  streamId: string | null
  source: CallShareSource | null
  audio: boolean
}

export interface CallSessionState {
  localStream: MediaStream | null
  remoteTracks: RemoteTrack[]
  /** The signalling channel is joined. Says nothing about media. */
  connected: boolean
  /** Per peer, the WebRTC connection state — the thing that does. */
  peerStates: Record<UUID, RTCPeerConnectionState>
  remoteMedia: Record<UUID, RemoteMedia>
  error: string | null
  micEnabled: boolean
  cameraEnabled: boolean
  /** Peers whose voice is carrying right now, debounced. */
  speaking: Record<UUID, boolean>
  /**
   * The browser says it is holding video back for want of bandwidth. Its own
   * word for it, not a guess from a bitrate we would have had to invent a
   * target for.
   */
  constrained: boolean
  /**
   * The camera was switched off to keep the audio, not by the person. Audio
   * is the meeting; video is decoration. Saying which of the two happened is
   * the difference between a client that looks broken and one that explains
   * itself.
   */
  videoPausedForBandwidth: boolean
  share: ShareSessionState
}

export interface ShareSessionState {
  /** This device can capture a screen at all. */
  supported: boolean
  /** Who is sharing, from the server. Null when nobody is. */
  state: ShareState | null
  /** What we are sending, when it is us. */
  local: MediaStream | null
  /** What we are receiving, when it is someone else. */
  remote: MediaStream | null
  /** Set when a claim was refused because someone else holds the surface. */
  conflict: boolean
  /** The last claim failed for a reason worth showing. */
  error: string | null
}

export interface StartShareOptions {
  audio?: boolean
  takeover?: boolean
}

/**
 * What the person chose before joining. The microphone track is always
 * acquired and muted when it is off, so unmuting mid-call needs no
 * renegotiation. The camera is not: asking for a camera someone has just
 * declined turns their light on for nothing.
 */
export interface CallPreferences {
  mic: boolean
  camera: boolean
  /** A specific microphone, from enumerateDevices; the default when unset. */
  audioDeviceId?: string
  videoDeviceId?: string
  /**
   * Chosen in the pre-join, acted on once the call is up. Whoever is about to
   * present knows it beforehand and should not have to hunt for the button
   * with everyone watching.
   */
  shareOnJoin?: boolean
}

const DEFAULT_PREFERENCES: CallPreferences = { mic: true, camera: true }

const SOCKET_PATH = '/socket/v1'

/** Voice this loud, for this long, is someone speaking rather than breathing. */
const SPEAKING_LEVEL = 0.02
const SPEAKING_IN_MS = 400
const SPEAKING_OUT_MS = 1000
const LEVEL_POLL_MS = 200

/** The browser has to say it is bandwidth-bound for this long before we do. */
const CONSTRAINED_FOR_MS = 5000
const STATS_POLL_MS = 1000

/**
 * A camera is worth a quarter of the resolution while a screen is going out.
 * The screen is the part that has to stay readable; a mesh carrying eight
 * cameras and a 1080p screen does not fit down an ordinary office line.
 */
const CAMERA_SCALE_WHILE_SHARING = 4
const CAMERA_BITRATE_WHILE_SHARING = 120_000
const CAMERA_FRAMERATE_WHILE_SHARING = 15

async function fetchSocketToken(): Promise<string> {
  const body = await api.post<SocketTokenResponse>('/auth/socket-token')
  return body.token
}

export function mediaConstraints(preferences: CallPreferences, video: boolean): MediaStreamConstraints {
  return {
    audio: preferences.audioDeviceId === undefined ? true : { deviceId: { exact: preferences.audioDeviceId } },
    video: !video
      ? false
      : preferences.videoDeviceId === undefined
        ? true
        : { deviceId: { exact: preferences.videoDeviceId } },
  }
}

/** Whether this device can capture a screen at all. Phones cannot, yet. */
export function screenShareSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices !== 'undefined' &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  )
}

/** Whether a screen track may be sent to this participant. */
export function canRenderShare(call: Call | null, userId: UUID): boolean {
  if (call === null) return false
  return call.participants.some(
    (participant) => participant.user?.id === userId && (participant.features ?? []).includes('screen_share'),
  )
}

export function useCallSession(
  call: Call | null,
  userId: UUID | undefined,
  enabled: boolean,
  preferences: CallPreferences = DEFAULT_PREFERENCES,
): CallSessionState & {
  hangUp: () => Promise<void>
  setMicEnabled: (on: boolean) => void
  setCameraEnabled: (on: boolean) => void
  startShare: (options?: StartShareOptions) => Promise<void>
  stopShare: () => Promise<void>
  dismissShareError: () => void
} {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null)
  const [remoteStreams, setRemoteStreams] = useState<Record<UUID, MediaStream[]>>({})
  const [connected, setConnected] = useState(false)
  const [peerStates, setPeerStates] = useState<Record<UUID, RTCPeerConnectionState>>({})
  const [remoteMedia, setRemoteMedia] = useState<Record<UUID, RemoteMedia>>({})
  const [error, setError] = useState<string | null>(null)
  const [micEnabled, setMicState] = useState(preferences.mic)
  const [cameraEnabled, setCameraState] = useState(preferences.camera)
  const [speaking, setSpeaking] = useState<Record<UUID, boolean>>({})
  const [constrained, setConstrained] = useState(false)
  const [videoPausedForBandwidth, setVideoPaused] = useState(false)
  const [shareState, setShareState] = useState<ShareState | null>(null)
  const [localShare, setLocalShare] = useState<MediaStream | null>(null)
  const [shareConflict, setShareConflict] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  // Read inside the effect so changing a toggle mid-call does not tear the
  // session down and dial again.
  const preferencesRef = useRef(preferences)
  preferencesRef.current = preferences

  const callRef = useRef<Call | null>(call)
  callRef.current = call

  const socketRef = useRef<Socket | null>(null)
  const channelRef = useRef<Channel | null>(null)
  const peersRef = useRef<Map<UUID, RTCPeerConnection>>(new Map())
  const pendingRef = useRef(new PendingCandidates())
  const localStreamRef = useRef<MediaStream | null>(null)
  const localShareRef = useRef<MediaStream | null>(null)
  const shareSendersRef = useRef<Map<UUID, RTCRtpSender[]>>(new Map())
  const renegotiateRef = useRef<(remoteId: UUID) => void>(() => undefined)
  const attachShareRef = useRef<(remoteId: UUID) => void>(() => undefined)

  const closePeer = useCallback((remoteId: UUID) => {
    const peer = peersRef.current.get(remoteId)
    if (peer === undefined) return
    peer.onicecandidate = null
    peer.ontrack = null
    peer.onconnectionstatechange = null
    peer.close()
    peersRef.current.delete(remoteId)
    pendingRef.current.forget(remoteId)
    shareSendersRef.current.delete(remoteId)
    setRemoteStreams((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setPeerStates((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setRemoteMedia((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
    setSpeaking((current) => {
      const { [remoteId]: _gone, ...rest } = current
      return rest
    })
  }, [])

  const stopLocalShareTracks = useCallback(() => {
    localShareRef.current?.getTracks().forEach((track) => track.stop())
    localShareRef.current = null
    setLocalShare(null)
  }, [])

  const cleanup = useCallback(() => {
    channelRef.current?.leave()
    channelRef.current = null
    socketRef.current?.disconnect()
    socketRef.current = null

    for (const remoteId of [...peersRef.current.keys()]) closePeer(remoteId)

    localStreamRef.current?.getTracks().forEach((track) => track.stop())
    localStreamRef.current = null
    stopLocalShareTracks()
    shareSendersRef.current.clear()
    setLocalStream(null)
    setRemoteStreams({})
    setPeerStates({})
    setRemoteMedia({})
    setSpeaking({})
    setShareState(null)
    setShareConflict(false)
    setShareError(null)
    setConstrained(false)
    setVideoPaused(false)
    setConnected(false)
  }, [closePeer, stopLocalShareTracks])

  const hangUp = useCallback(async () => {
    if (call === null) return
    cleanup()
    await leaveCall(call.id)
  }, [call, cleanup])

  // The server's participant list drives who we hold a connection to. A
  // newly joined peer offers to us, so only departures are acted on here.
  useEffect(() => {
    if (call === null) return
    for (const remoteId of departedPeerIds(peersRef.current.keys(), call, userId)) {
      closePeer(remoteId)
    }
  }, [call, closePeer, userId])

  // The call itself carries who is sharing, so a tab that reloads mid-share,
  // or someone who joined after it started, is not left waiting for a signal
  // that already went out.
  useEffect(() => {
    if (call === null) return
    const holder = call.sharing_user_id ?? null
    setShareState((current) => {
      if (holder === null) return null
      if (current !== null && current.userId === holder && current.streamId !== null) return current
      return {
        userId: holder,
        streamId: call.sharing_stream_id ?? null,
        source: call.sharing_source ?? null,
        audio: false,
      }
    })
  }, [call])

  const callId = call?.id

  useEffect(() => {
    if (!enabled || callId === undefined || userId === undefined) {
      cleanup()
      return
    }

    let cancelled = false

    const run = async (): Promise<void> => {
      try {
        const joined = await joinCall(callId)
        if (cancelled) return

        const ice = await fetchIceServers()
        if (cancelled) return

        const wanted = preferencesRef.current
        const stream = await navigator.mediaDevices.getUserMedia(
          mediaConstraints(wanted, joined.media === 'video' && wanted.camera),
        )
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        stream.getAudioTracks().forEach((track) => (track.enabled = wanted.mic))
        // A camera is motion; a shared screen is detail. Telling the encoder
        // which costs nothing and is the difference between readable text and
        // a smooth blur.
        stream.getVideoTracks().forEach((track) => (track.contentHint = 'motion'))
        setMicState(wanted.mic)
        setCameraState(stream.getVideoTracks().length > 0)

        localStreamRef.current = stream
        setLocalStream(stream)

        const token = await fetchSocketToken()
        if (cancelled) return

        const socket = new Socket(SOCKET_PATH, { params: { token } })
        socketRef.current = socket
        socket.connect()

        const channel = socket.channel(`call:${callId}`)
        channelRef.current = channel

        const sendSignal = (targetUserId: UUID, kind: string, payload: Record<string, unknown>): void => {
          channel.push('signal', { target_user_id: targetUserId, kind, payload })
        }

        const announceMedia = (): void => {
          const audio = localStreamRef.current?.getAudioTracks()[0]
          const video = localStreamRef.current?.getVideoTracks()[0]
          channel.push('media', {
            audio_muted: audio === undefined ? true : !audio.enabled,
            video_enabled: video !== undefined && video.enabled,
          })
        }

        const offerTo = async (remoteId: UUID, restart = false): Promise<void> => {
          const peer = ensurePeer(remoteId)
          const offer = await peer.createOffer(restart ? { iceRestart: true } : undefined)
          await peer.setLocalDescription(offer)
          sendSignal(remoteId, 'offer', { sdp: offer })
        }

        // Adding or removing a screen track changes what the connection
        // carries, so it has to be offered again. An offer sent while the
        // last one is still in flight is refused by the browser, so a
        // connection that is not stable is left to settle and tried again.
        const renegotiate = (remoteId: UUID, attempt = 0): void => {
          const peer = peersRef.current.get(remoteId)
          if (peer === undefined) return
          if (peer.signalingState !== 'stable') {
            if (attempt < 5) window.setTimeout(() => renegotiate(remoteId, attempt + 1), 300)
            return
          }
          void offerTo(remoteId).catch((caught: unknown) => {
            console.warn('renegotiation failed', caught)
          })
        }
        renegotiateRef.current = renegotiate

        // Only to a peer that has said it can draw one. The check is the
        // participant's own word, from the call we are holding.
        const attachShareTo = (remoteId: UUID): void => {
          const share = localShareRef.current
          const peer = peersRef.current.get(remoteId)
          if (share === null || peer === undefined) return
          if (!canRenderShare(callRef.current, remoteId)) return
          if (shareSendersRef.current.has(remoteId)) return

          const senders = share.getTracks().map((track) => peer.addTrack(track, share))
          shareSendersRef.current.set(remoteId, senders)
          renegotiate(remoteId)
        }
        attachShareRef.current = attachShareTo

        const ensurePeer = (remoteId: UUID): RTCPeerConnection => {
          const existing = peersRef.current.get(remoteId)
          if (existing !== undefined) return existing

          const peer = new RTCPeerConnection({ iceServers: ice.ice_servers })
          peersRef.current.set(remoteId, peer)
          setPeerStates((current) => ({ ...current, [remoteId]: peer.connectionState }))

          for (const track of stream.getTracks()) {
            peer.addTrack(track, stream)
          }

          peer.onicecandidate = (event) => {
            if (event.candidate !== null) {
              sendSignal(remoteId, 'ice', { candidate: event.candidate.toJSON() })
            }
          }

          // Every stream a peer sends is kept, not just the first: the second
          // one is a screen, and which is which is answered by the share
          // signal rather than by arrival order.
          peer.ontrack = (event) => {
            const [remoteStream] = event.streams
            if (remoteStream === undefined) return
            setRemoteStreams((current) => {
              const held = current[remoteId] ?? []
              if (held.some((entry) => entry.id === remoteStream.id)) return current
              return { ...current, [remoteId]: [...held, remoteStream] }
            })
          }

          // A failed connection is retried once with an ICE restart — a
          // network change on either side looks like this — before it is
          // reported as such. `disconnected` is transient and left to
          // recover on its own.
          let restarted = false
          peer.onconnectionstatechange = () => {
            setPeerStates((current) => ({ ...current, [remoteId]: peer.connectionState }))
            if (peer.connectionState === 'failed' && !restarted) {
              restarted = true
              void offerTo(remoteId, true).catch(() => undefined)
            }
            if (peer.connectionState === 'connected') {
              restarted = false
              // A peer that arrives while a screen is already going out gets
              // it as soon as it is there to get it.
              attachShareTo(remoteId)
            }
          }

          return peer
        }

        channel.on('signal', (payload: Record<string, unknown>) => {
          const fromUserId = payload['from_user_id']
          const kind = payload['kind']
          const body = payload['payload']
          if (typeof fromUserId !== 'string' || typeof kind !== 'string' || typeof body !== 'object' || body === null) {
            return
          }

          void (async () => {
            const peer = ensurePeer(fromUserId)
            try {
              if (kind === 'offer' && typeof (body as { sdp?: unknown }).sdp === 'object') {
                const sdp = (body as { sdp: RTCSessionDescriptionInit }).sdp
                await peer.setRemoteDescription(sdp)
                for (const candidate of pendingRef.current.drain(fromUserId)) {
                  await peer.addIceCandidate(candidate)
                }
                const answer = await peer.createAnswer()
                await peer.setLocalDescription(answer)
                sendSignal(fromUserId, 'answer', { sdp: answer })
              } else if (kind === 'answer' && typeof (body as { sdp?: unknown }).sdp === 'object') {
                await peer.setRemoteDescription((body as { sdp: RTCSessionDescriptionInit }).sdp)
                for (const candidate of pendingRef.current.drain(fromUserId)) {
                  await peer.addIceCandidate(candidate)
                }
              } else if (kind === 'ice' && typeof (body as { candidate?: unknown }).candidate === 'object') {
                const candidate = (body as { candidate: RTCIceCandidateInit }).candidate
                if (peer.remoteDescription === null) {
                  pendingRef.current.hold(fromUserId, candidate)
                } else {
                  await peer.addIceCandidate(candidate)
                }
              }
            } catch (caught) {
              // One bad candidate is not the call; it is logged, not shown.
              console.warn('call signal failed', kind, caught)
            }
          })()
        })

        channel.on('media', (payload: Record<string, unknown>) => {
          const from = payload['user_id']
          if (typeof from !== 'string' || from === userId) return
          setRemoteMedia((current) => ({
            ...current,
            [from]: {
              audioMuted: payload['audio_muted'] === true,
              videoEnabled: payload['video_enabled'] !== false,
            },
          }))
        })

        // The server owns the answer to who is sharing. Everyone on the topic
        // is told, the sharer included, so no client decides it for itself.
        channel.on('share.changed', (payload: Record<string, unknown>) => {
          const holder = payload['user_id']
          if (typeof holder !== 'string') {
            setShareState(null)
            if (localShareRef.current !== null) stopLocalShareTracks()
            return
          }

          const streamId = payload['stream_id']
          setShareState({
            userId: holder,
            streamId: typeof streamId === 'string' ? streamId : null,
            source: (payload['source'] as CallShareSource | null) ?? null,
            audio: payload['audio'] === true,
          })

          // Someone took the surface from us. The capture stops here rather
          // than going out to nobody.
          if (holder !== userId && localShareRef.current !== null) {
            stopLocalShareTracks()
            for (const [remoteId, senders] of shareSendersRef.current) {
              const peer = peersRef.current.get(remoteId)
              senders.forEach((sender) => {
                try {
                  peer?.removeTrack(sender)
                } catch {
                  // The peer may already be gone; nothing to undo.
                }
              })
              renegotiate(remoteId)
            }
            shareSendersRef.current.clear()
            setShareError('Someone else took over the shared screen.')
          }
        })

        channel.onError(() => {
          if (!cancelled) setConnected(false)
        })

        // Everyone already in the call gets an offer from us; anyone who
        // joins later offers to us. Phoenix re-sends the join after a
        // dropped socket and this fires again, so a peer whose connection
        // died with the socket is dialled afresh while one that survived is
        // left alone.
        channel
          .join()
          .receive('ok', () => {
            if (cancelled) return
            setConnected(true)
            announceMedia()
            for (const remoteId of joinedPeerIds(joined, userId)) {
              const existing = peersRef.current.get(remoteId)
              if (existing !== undefined && existing.connectionState === 'connected') continue
              if (existing !== undefined) closePeer(remoteId)
              void offerTo(remoteId).catch((caught: unknown) => {
                console.warn('offer failed', caught)
              })
            }
          })
          .receive('error', () => {
            if (!cancelled) setError('Could not join the call channel.')
          })
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Could not start the call.')
        }
      }
    }

    void run()

    return () => {
      cancelled = true
      cleanup()
    }
  }, [callId, cleanup, closePeer, enabled, stopLocalShareTracks, userId])

  // ── Who is speaking ────────────────────────────────────────────────────────
  //
  // From the receivers themselves rather than a web audio graph per peer: the
  // level is already in the RTP header, and reading it there costs nothing
  // where decoding a second copy of every stream would.
  useEffect(() => {
    if (!connected) return

    const since: Record<UUID, number> = {}
    const timer = window.setInterval(() => {
      const now = Date.now()
      const loud: Record<UUID, boolean> = {}

      for (const [remoteId, peer] of peersRef.current) {
        let level = 0
        for (const receiver of peer.getReceivers()) {
          if (receiver.track.kind !== 'audio') continue
          const sources = receiver.getSynchronizationSources?.() ?? []
          for (const source of sources) level = Math.max(level, source.audioLevel ?? 0)
        }
        loud[remoteId] = level >= SPEAKING_LEVEL
      }

      setSpeaking((current) => {
        let changed = false
        const next: Record<UUID, boolean> = { ...current }

        for (const [remoteId, isLoud] of Object.entries(loud)) {
          const wasSpeaking = current[remoteId] === true
          if (isLoud) {
            since[remoteId] = since[remoteId] ?? now
            if (!wasSpeaking && now - (since[remoteId] ?? now) >= SPEAKING_IN_MS) {
              next[remoteId] = true
              changed = true
            }
          } else {
            const quietSince = since[remoteId]
            if (quietSince !== undefined) delete since[remoteId]
            if (wasSpeaking && now - (quietSince ?? now) >= SPEAKING_OUT_MS) {
              next[remoteId] = false
              changed = true
            }
          }
        }

        return changed ? next : current
      })
    }, LEVEL_POLL_MS)

    return () => window.clearInterval(timer)
  }, [connected])

  // ── A line too narrow for what we are sending ─────────────────────────────
  useEffect(() => {
    if (!connected) return

    let limitedSince: number | null = null
    const timer = window.setInterval(() => {
      void (async () => {
        let limited = false
        for (const peer of peersRef.current.values()) {
          const stats = await peer.getStats().catch(() => null)
          stats?.forEach((report) => {
            const outbound = report as { type?: string; kind?: string; qualityLimitationReason?: string }
            if (outbound.type === 'outbound-rtp' && outbound.kind === 'video') {
              if (outbound.qualityLimitationReason === 'bandwidth') limited = true
            }
          })
        }

        const now = Date.now()
        if (limited) {
          limitedSince = limitedSince ?? now
          if (now - limitedSince >= CONSTRAINED_FOR_MS) setConstrained(true)
        } else {
          limitedSince = null
          setConstrained(false)
        }
      })()
    }, STATS_POLL_MS)

    return () => window.clearInterval(timer)
  }, [connected])

  // ── The camera, while a screen is going out ───────────────────────────────
  const sharingNow = shareState !== null
  useEffect(() => {
    for (const peer of peersRef.current.values()) {
      for (const sender of peer.getSenders()) {
        if (sender.track?.kind !== 'video') continue
        // The screen sender is not capped; it is the thing being read.
        const isShare = [...shareSendersRef.current.values()].some((senders) => senders.includes(sender))
        if (isShare) continue

        const parameters = sender.getParameters()
        if (parameters.encodings === undefined || parameters.encodings.length === 0) continue
        parameters.encodings[0] = {
          ...parameters.encodings[0],
          scaleResolutionDownBy: sharingNow ? CAMERA_SCALE_WHILE_SHARING : 1,
          maxBitrate: sharingNow ? CAMERA_BITRATE_WHILE_SHARING : undefined,
          maxFramerate: sharingNow ? CAMERA_FRAMERATE_WHILE_SHARING : undefined,
        }
        void sender.setParameters(parameters).catch(() => undefined)
      }
    }
  }, [sharingNow])

  const announce = useCallback(() => {
    const channel = channelRef.current
    if (channel === null) return
    const audio = localStreamRef.current?.getAudioTracks()[0]
    const video = localStreamRef.current?.getVideoTracks()[0]
    channel.push('media', {
      audio_muted: audio === undefined ? true : !audio.enabled,
      video_enabled: video !== undefined && video.enabled,
    })
  }, [])

  const setMicEnabled = useCallback(
    (on: boolean) => {
      localStreamRef.current?.getAudioTracks().forEach((track) => (track.enabled = on))
      setMicState(on)
      announce()
    },
    [announce],
  )

  // Only the tracks already published can be switched; a call joined without a
  // camera has none, and turning one on would need renegotiating with every
  // peer in the mesh.
  const setCameraEnabled = useCallback(
    (on: boolean) => {
      const tracks = localStreamRef.current?.getVideoTracks() ?? []
      if (tracks.length === 0) return
      tracks.forEach((track) => (track.enabled = on))
      setCameraState(on)
      if (on) setVideoPaused(false)
      announce()
    },
    [announce],
  )

  // Cameras go before audio does, and only once: someone who turns theirs back
  // on has overruled us, and is not overruled back a second later.
  const pausedOnceRef = useRef(false)
  useEffect(() => {
    if (!constrained || pausedOnceRef.current) return
    if (!cameraEnabled) return
    const tracks = localStreamRef.current?.getVideoTracks() ?? []
    if (tracks.length === 0) return

    pausedOnceRef.current = true
    tracks.forEach((track) => (track.enabled = false))
    setCameraState(false)
    setVideoPaused(true)
    announce()
  }, [announce, cameraEnabled, constrained])

  const stopShare = useCallback(async (): Promise<void> => {
    const channel = channelRef.current
    if (localShareRef.current === null) return

    for (const [remoteId, senders] of shareSendersRef.current) {
      const peer = peersRef.current.get(remoteId)
      senders.forEach((sender) => {
        try {
          peer?.removeTrack(sender)
        } catch {
          // Already gone; the renegotiation below still settles it.
        }
      })
      renegotiateRef.current(remoteId)
    }
    shareSendersRef.current.clear()
    stopLocalShareTracks()

    await new Promise<void>((resolve) => {
      if (channel === null) {
        resolve()
        return
      }
      channel
        .push('share', { action: 'stop' })
        .receive('ok', () => resolve())
        .receive('error', () => resolve())
        .receive('timeout', () => resolve())
    })
  }, [stopLocalShareTracks])

  /**
   * Capture first, claim second. The browser's own picker is the last point
   * where a person can change their mind, and nothing should have been said
   * to the call before they have made it through that.
   */
  const startShare = useCallback(
    async (options: StartShareOptions = {}): Promise<void> => {
      const channel = channelRef.current
      if (channel === null || !screenShareSupported()) return

      setShareConflict(false)
      setShareError(null)

      let captured: MediaStream
      try {
        captured = await navigator.mediaDevices.getDisplayMedia({
          video: { frameRate: 15 },
          audio: options.audio === true,
        })
      } catch (caught) {
        // Cancelling the picker is the commonest path through here and is not
        // a failure worth a line of red.
        if (caught instanceof Error && caught.name === 'NotAllowedError') return
        setShareError('That screen could not be captured.')
        return
      }

      // Text over motion: the encoder should hold detail rather than
      // smoothness when the two compete.
      captured.getVideoTracks().forEach((track) => (track.contentHint = 'detail'))

      // The browser knows what was picked; the meeting should say so rather
      // than call every share "a screen".
      const surface = (captured.getVideoTracks()[0]?.getSettings() as { displaySurface?: string } | undefined)
        ?.displaySurface
      const source: CallShareSource = surface === 'window' ? 'window' : surface === 'browser' ? 'tab' : 'screen'

      const claimed = await new Promise<'ok' | 'conflict' | 'error'>((resolve) => {
        channel
          .push('share', {
            action: 'start',
            source,
            audio: options.audio === true,
            takeover: options.takeover === true,
            stream_id: captured.id,
          })
          .receive('ok', () => resolve('ok'))
          .receive('error', (reply: { error?: { code?: string } }) =>
            resolve(reply?.error?.code === 'conflict' ? 'conflict' : 'error'),
          )
          .receive('timeout', () => resolve('error'))
      })

      if (claimed !== 'ok') {
        captured.getTracks().forEach((track) => track.stop())
        if (claimed === 'conflict') setShareConflict(true)
        else setShareError('The screen could not be shared.')
        return
      }

      localShareRef.current = captured
      setLocalShare(captured)

      // Stopping from the browser's own bar is the same as stopping from
      // ours; the call is told either way.
      captured.getVideoTracks().forEach((track) => {
        track.onended = () => {
          void stopShare()
        }
      })

      for (const remoteId of peersRef.current.keys()) attachShareRef.current(remoteId)
    },
    [stopShare],
  )

  const dismissShareError = useCallback(() => {
    setShareConflict(false)
    setShareError(null)
  }, [])

  // What a peer's face is, and what its screen is, once the server has said
  // which stream is which.
  const remoteTracks = useMemo((): RemoteTrack[] => {
    return Object.entries(remoteStreams).flatMap(([remoteId, streams]) => {
      const face =
        shareState !== null && shareState.userId === remoteId && shareState.streamId !== null
          ? streams.find((entry) => entry.id !== shareState.streamId)
          : streams[0]
      const chosen = face ?? streams[0]
      return chosen === undefined ? [] : [{ userId: remoteId, stream: chosen }]
    })
  }, [remoteStreams, shareState])

  const remoteShare = useMemo((): MediaStream | null => {
    if (shareState === null || shareState.userId === userId) return null
    const streams = remoteStreams[shareState.userId] ?? []
    if (shareState.streamId !== null) {
      return streams.find((entry) => entry.id === shareState.streamId) ?? null
    }
    // Before the stream is named — a reload mid-share — the second stream is
    // the only candidate there is.
    return streams.length > 1 ? (streams[streams.length - 1] ?? null) : null
  }, [remoteStreams, shareState, userId])

  const share = useMemo(
    (): ShareSessionState => ({
      supported: screenShareSupported(),
      state: shareState,
      local: localShare,
      remote: remoteShare,
      conflict: shareConflict,
      error: shareError,
    }),
    [localShare, remoteShare, shareConflict, shareError, shareState],
  )

  return {
    localStream,
    remoteTracks,
    connected,
    peerStates,
    remoteMedia,
    error,
    micEnabled,
    cameraEnabled,
    speaking,
    constrained,
    videoPausedForBandwidth,
    share,
    hangUp,
    setMicEnabled,
    setCameraEnabled,
    startShare,
    stopShare,
    dismissShareError,
  }
}
